import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'design-system/styles';
import {
  Annotation,
  AnnotationGeometry,
  AnnotationPathCurrent,
  PolygonVertices,
} from '@scale/shared/types/annotation';
import { Point2D } from '@scale/shared/types/math';
import { getBoundingBox } from '@scale/shared/utils/geometry';
import _ from 'lodash';
import {
  getSvgPathDForPolyline,
  getSvgPathDForPolylineArrow,
} from '../labeler/geometry/PathLabeler/pathUtils';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { USE_LITE_UI } from '@scale/frontend-lib/constants';
import { isGisAssignment } from '@scale/shared/assignmentJson/isGisAssignment';

const useStyles = makeStyles({
  thumbnail: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderRadius: 4,
    transition: 'all 0.2s',
  },
  thumbnailSvg: {
    width: '100%',
    height: '100%',
    '& path': {
      transition: 'backgroundColor 0.2s, viewBox 0.2s',
    },
  },
});

function getInfo(vertices: Point2D[]) {
  const cornerPoints = getBoundingBox(vertices);
  const { x: minX, y: minY } = cornerPoints[0];
  const { x: maxX, y: maxY } = cornerPoints[2];
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const rad = 0.6 * Math.max(maxX - minX, maxY - minY, 1);

  return { cx, cy, rad };
}

function getViewBox(vertices: Point2D[]) {
  const { cx, cy, rad } = getInfo(vertices);
  const padding = rad * 0.15;
  const paddedRad = rad + padding;
  return `${cx - paddedRad} ${cy - paddedRad} ${2 * paddedRad} ${2 * paddedRad}`;
}

function getStrokeWidth(vertices: Point2D[]) {
  const { rad } = getInfo(vertices);
  return 0.15 * rad;
}

interface AnnotationListItemHeaderThumbnailProps {
  isBase?: boolean;
  isEmpty?: boolean;
  annotation?: Annotation | AnnotationPathCurrent<Annotation>;
  color: string;
  assignment?: AssignmentJson;
  size?: number;
  useUnified?: boolean;
}

const AnnotationListItemHeaderThumbnail: React.FC<AnnotationListItemHeaderThumbnailProps> = ({
  isBase,
  isEmpty,
  annotation,
  color,
  assignment,
  size,
  useUnified,
}) => {
  const classes = useStyles();

  let thumbnailContent: React.ReactNode;
  let backgroundColor = color;
  let borderColor = color;
  if (isEmpty) {
    backgroundColor = 'transparent';
  } else if (isBase) {
    thumbnailContent = <FontAwesomeIcon icon="history" />;
  } else if (
    annotation?.geometry === AnnotationGeometry.Polygon ||
    annotation?.geometry === AnnotationGeometry.Line
  ) {
    const isPolygon = annotation.geometry === AnnotationGeometry.Polygon;
    const currentFrame =
      'currentFrameAnnotation' in annotation ? annotation.currentFrameAnnotation : annotation;
    const vertices: Point2D[] = _.get(currentFrame, 'vertices') ?? [];
    const forceLiteUI = isGisAssignment(assignment);

    if (!USE_LITE_UI && !forceLiteUI && vertices.length >= 2) {
      const multiplePolygonVertices: PolygonVertices[] = _.get(
        currentFrame,
        'multiple_polygon_vertices',
      );
      const directedLinesEnabled = _.includes(
        _.get(assignment?.params, 'directed_lines'),
        annotation.label,
      );

      // lengthy calculation, skip in LITE
      const viewBox = getViewBox(vertices);
      const d = getSvgPathDForPolyline(vertices, { closed: isPolygon, multiplePolygonVertices });
      const strokeWidth = getStrokeWidth(vertices);
      const arrowSize = strokeWidth * 4;
      const fillOpacity = isPolygon ? 0.2 : 0;
      thumbnailContent = (
        <svg className={classes.thumbnailSvg} viewBox={viewBox}>
          <path
            stroke={color}
            fill={color}
            fillOpacity={fillOpacity}
            strokeWidth={strokeWidth}
            d={d}
          />
          {directedLinesEnabled && (
            <path
              stroke={'#555555CC'}
              fill={color}
              strokeWidth={strokeWidth * 0.5}
              d={getSvgPathDForPolylineArrow(vertices, { arrowSize })}
            />
          )}
        </svg>
      );
      backgroundColor = 'transparent';
      borderColor = 'transparent';
      // TODO support custom icons for points, box, cuboid
    } else {
      // use solid block for unsupported geometries
      thumbnailContent = null;
    }
  } else {
    // use solid block for unsupported geometries
    thumbnailContent = null;
  }

  if (useUnified) {
    switch (annotation?.geometry) {
      case AnnotationGeometry.Box:
        thumbnailContent = <FontAwesomeIcon icon="draw-square" color={color} size="lg" />;
        break;
      case AnnotationGeometry.Cuboid:
        thumbnailContent = <FontAwesomeIcon icon="cube" color={color} size="lg" />;
        break;
      case AnnotationGeometry.Point:
        thumbnailContent = <FontAwesomeIcon icon="dot-circle" color={color} size="lg" />;
        break;
      case AnnotationGeometry.Polygon:
        thumbnailContent = <FontAwesomeIcon icon="draw-polygon" color={color} size="lg" />;
        break;
    }

    if (thumbnailContent) {
      backgroundColor = 'transparent';
      borderColor = 'transparent';
    }
  }

  return (
    <>
      <div
        style={{ backgroundColor, borderColor, ...(size && { height: size, width: size }) }}
        className={classes.thumbnail}
      >
        {thumbnailContent}
      </div>
    </>
  );
};
export default React.memo(AnnotationListItemHeaderThumbnail);
